import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import cx from 'classnames';
import {priority} from '@priority-software/priority-proto-api/lib';
import {eventPrevented} from '../../../../utils/eventPrevented';
import {Column} from '../../../../stores/Form/Column';
import {codes, keys} from '../../../../constants/KeyCodes';
import {FormInputButton} from '../FormInputButton';
import {
    chooseImageByZoomType,
    fieldValueToEmail,
    fieldValueToUrl
} from '../../utils/formInputButton';
import {fieldValue} from '../../../../stores/Form/Field';
import {isOperatorField} from '../../utils/filters';
import {CellSearchButton, OpenSearchDropdownAPI} from '../CellSearchButton';
import {isZoomable, zoom} from '../../actions/zoom';
import {listTypes} from '../SelectListModal/enum';
import {isDate} from '../../utils/date';
import {undoAction} from '../../actions/undo';
import {showFormFieldHelp} from '../../utils/help';
import {SuspendedJobTypes} from '../../consts/suspendedJobTypes';
import {FormInputComponent} from '../Input';
import {getOperatorsSearchObject} from './constants';
import style from '../../styles/formInput.module.scss';
import {formsStore} from '../../../../stores/Form/FormsStore';

interface FormInputProps {
    initValue: fieldValue;
    origValue: fieldValue;
    column: Column;
    rowIndex: number;
    searchMode: boolean;
    isActive: boolean;
    updateSearchValue: (val: string, colId: number, rowIndex: number) => void;
    updateCurrentValue: (val: any) => void;
    onMaxWidthMode?: () => void;
    onFocus?: (el) => void;
    openSelectListModal: (listType: listTypes) => void;
    getCellWrap?: () => HTMLDivElement | null;
    componentClasses?: string;
}

export const FormInput = memo((props: FormInputProps) => {
    const {
        column,
        rowIndex,
        searchMode,
        initValue,
        isActive,
        updateSearchValue,
        onMaxWidthMode,
        updateCurrentValue,
        onFocus,
        getCellWrap,
        openSelectListModal,
        componentClasses
    } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const f6Timer = useRef(null);
    const fieldSearchRef = useRef<OpenSearchDropdownAPI>(null);

    const [value, setValue] = useState(initValue);

    const setMaxWidthMode = useCallback(
        (flag: boolean) => {
            column.setMaxWidthMode(flag);
            if (onMaxWidthMode) onMaxWidthMode();
        },
        [onMaxWidthMode, column]
    );

    useEffect(() => {
        if (column.Type !== priority.netitems.form.ColumnType.Boolean) {
            formsStore.updateSidePanelSubformsState(rowIndex);
        }
    }, [rowIndex, column]);

    useEffect(() => {
        if (isActive) {
            inputRef.current?.focus({preventScroll: true});
            if (!column.editMode) {
                inputRef.current?.select?.();
            }
        }
    }, [isActive, column]);

    const setEditMode = editMode => {
        column.setEditMode(editMode);
        if (editMode && !column.maxWidthMode) {
            setMaxWidthMode(true);
        }
    };

    const toggleEditMode = () => {
        setEditMode(!column.editMode);
    };

    const processEventCodes = async (event: KeyboardEvent) => {
        // `code` property doesn't exist in React Synthetic event
        // and should be extracted from the `nativeEvent` property
        if (event.ctrlKey && event.code === codes.Z) {
            event.preventDefault();
            await undoAction();
        }
    };

    const onDropdownClose = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const {key} = event;
        const {ZoomType} = priority.netitems.form;
        const zoomType = getZoomType(column);
        switch (key) {
            case keys.F1:
                if (!event.ctrlKey && !event.metaKey) {
                    showFormFieldHelp({
                        anchor: getCellWrap(),
                        column
                    });
                }
                break;
            case keys.F2:
                inputRef.current.setSelectionRange(-1, -1);
                toggleEditMode();
                setMaxWidthMode(true);
                break;
            case keys.F6:
                if (zoomType === ZoomType.ZoomZoom) {
                    zoom(column.parentForm, column);
                } else if (zoomType === ZoomType.SelectZoom) {
                    if (f6Timer.current && isZoomable(column)) {
                        f6Timer.current = clearTimeout(f6Timer.current);
                        zoom(column.parentForm, column);
                        return;
                    }
                    const isForceSearch = event.ctrlKey;
                    f6Timer.current = setTimeout(() => {
                        //here we'll open the searchDropdown
                        const fieldSearchAPI = fieldSearchRef.current;
                        if (fieldSearchAPI) {
                            if (!fieldSearchAPI.isOpen) {
                                fieldSearchAPI.openSearchDropdown(isForceSearch);
                            }
                        }
                        f6Timer.current = clearTimeout(f6Timer.current);
                    }, 500);
                } else if (zoomType !== ZoomType.NoneZoom) {
                    processNonSearchZooms(zoomType);
                }
                break;
            case keys.SHIFT:
                setMaxWidthMode(true);
                break;
            case keys.SPACE:
                if (column.isReadonly) {
                    event.preventDefault();
                }
                if (zoomType === ZoomType.SelectZoom) {
                    const fieldSearchAPI = fieldSearchRef.current;
                    if (fieldSearchAPI) {
                        if (!fieldSearchAPI.isOpen && value === '') {
                            eventPrevented(event, true);
                            fieldSearchAPI.openSearchDropdown();
                        }
                    }
                } else if (zoomType !== ZoomType.NoneZoom && zoomType !== ZoomType.ZoomZoom) {
                    processNonSearchZooms(zoomType);
                }
                break;
            case keys.HOME:
                if (column.editMode) {
                    eventPrevented(event, true);
                    inputRef.current.setSelectionRange(0, 0);
                }
                break;
            case keys.END:
                if (column.editMode) {
                    eventPrevented(event, true);
                    const endPosition = inputRef.current?.value.length || 0;
                    inputRef.current.setSelectionRange(endPosition, endPosition);
                }
                break;
            default:
                break;
        }
        processEventCodes(event.nativeEvent);
    };

    const showFormInputButton = (): boolean => {
        return column.Zoom !== priority.netitems.form.ZoomType.NoneZoom && isZoomable(column);
    };

    const getZoomType = (column: Column): priority.netitems.form.ZoomType => {
        return column.Relcol ? column.relatedIdColumn.Zoom : column.Zoom;
    };

    const onInputButtonClick = (column: Column) => {
        const zoomType = getZoomType(column);
        const {ZoomType} = priority.netitems.form;
        if (zoomType === ZoomType.SelectZoom || zoomType === ZoomType.ZoomZoom) {
            if (!column.Zoomfname && !column.Relcol && !value) {
                openSelectListModal(listTypes.DirectActivationsF);
            } else {
                formsStore.isRecording &&
                    formsStore.setEventForRecordingMacro({
                        type: SuspendedJobTypes.Zoom,
                        column: column
                    });
                zoom(column.parentForm, column);
            }
        } else {
            processNonSearchZooms(zoomType);
        }
    };

    const processNonSearchZooms = zoomType => {
        const {ZoomType} = priority.netitems.form;
        if (zoomType === ZoomType.URLZoom && value) {
            window.open(fieldValueToUrl(value));
        } else if (zoomType === ZoomType.EMailZoom) {
            window.open(fieldValueToEmail(value));
        }
    };

    const handleChange = useCallback(
        (value: any) => {
            if (searchMode) {
                updateSearchValue(value, column.id, rowIndex);
            } else {
                if (updateCurrentValue) {
                    updateCurrentValue(value);
                }
            }
            setValue(value);
        },
        [column, updateSearchValue, searchMode, rowIndex, updateCurrentValue]
    );

    const setActiveEl = useCallback(
        e => {
            onFocus(e.currentTarget);
        },
        [onFocus]
    );

    const onSelect = useCallback(
        async (val: string) => {
            const relatedColumn = column.relatedIdColumn;
            if (relatedColumn) {
                await formsStore.currentScreen.updateRelatedField(relatedColumn.id, val);
                formsStore.setEventForRecordingMacro({
                    type: SuspendedJobTypes.FieldValue,
                    value: val
                });
            } else {
                handleChange(val);
            }
        },
        [handleChange, column]
    );

    if (!column.hidden) {
        const zoomType = getZoomType(column);
        const isBoolean = column.Type === priority.netitems.form.ColumnType.Boolean;

        const hasCellButtonSearch =
            (!column.isReadonly &&
                !isBoolean &&
                column.Zoom === priority.netitems.form.ZoomType.SelectZoom) ||
            isOperatorField(searchMode, rowIndex);

        const hasFormInputButton = showFormInputButton() && !isDate(column.Type) && !isBoolean;

        return (
            <div
                className={cx(style.inputWrapper, {
                    [style.disabledInput]: column.isReadonly || column.parentForm.isReadonly,
                    [style.searchInput]: searchMode && isActive,
                    [style.active]: isActive,
                    [style.booleanInput]: isBoolean && !isOperatorField(searchMode, rowIndex),
                    [componentClasses]: !!componentClasses
                })}
            >
                <FormInputComponent
                    column={column}
                    searchMode={searchMode}
                    rowIndex={rowIndex}
                    value={value}
                    handleKeyDown={handleKeyDown}
                    handleChange={handleChange}
                    inputRef={inputRef}
                    isActive={isActive}
                    processEventCodes={processEventCodes}
                    setMaxWidthMode={setMaxWidthMode}
                    setEditMode={setEditMode}
                    onFocus={setActiveEl}
                />

                {hasCellButtonSearch && isActive && (
                    <CellSearchButton
                        ref={fieldSearchRef}
                        onClose={onDropdownClose}
                        onSelect={onSelect}
                        getCellWrap={getCellWrap}
                        column={column}
                        searchValue={value as string}
                        zindex={10010}
                        prefetchedResults={
                            isOperatorField(searchMode, rowIndex) && getOperatorsSearchObject()
                        }
                    />
                )}

                {hasFormInputButton && isActive && (
                    <FormInputButton
                        isPrimary
                        icon={chooseImageByZoomType(zoomType, column.isReadonly)}
                        onClick={() => onInputButtonClick(column)}
                    />
                )}
            </div>
        );
    }
    return null;
});
